import React, { Component } from 'react';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { FormHelperText } from '@mui/material';

import {
  PhoneUtil,
} from 'doctivity-shared/utils';

import axiosInstance from 'utils/axiosUtil';
import TextWidget from 'components/material-form/components/TextWidget';

import {
  DialogForm,
} from 'components';

const styles = () => ({
});

const DUPLICATE_NPI_MESSAGE = 'This NPI is already assigned to another organization in Doctivity. NPIs must be unique, so this record cannot be saved.';
const NPI_LENGTH_MESSAGE = 'NPI must be exactly 10 digits.';

// Custom widget for the NPI field only. Reuses the standard text input (so the
// red error state and styling match the rest of the form) and renders the
// NPI error message (invalid length or duplicate) directly beneath the field,
// matching the Provider dialogs. The error is passed via formContext rather than
// the form's error system so the message isn't also rendered by the default
// field error template.
function NpiWidget(props) {
  const { formContext, registry, rawErrors } = props;
  const ctx = formContext || (registry && registry.formContext) || {};
  const npiError = ctx.npiError;
  // Force the field red when there's an NPI error; otherwise pass through any
  // real validation errors (e.g. the required check) untouched.
  const widgetErrors = npiError ? [npiError] : rawErrors;
  return (
    <>
      <TextWidget {...props} rawErrors={widgetErrors} />
      {npiError && (
        <FormHelperText error sx={{ ml: '14px', mt: '12px', mb: '8px' }}>
          {npiError}
        </FormHelperText>
      )}
    </>
  );
}

NpiWidget.propTypes = {
  rawErrors: PropTypes.array,
  formContext: PropTypes.object,
  registry: PropTypes.object,
};

const schema = {
  type: 'object',
  required: ['name', 'npi', 'facility_type', 'specialty'],
  properties: {
    name: { type: 'string', title: 'Name' },
    npi: { type: 'string', title: 'NPI #', maxLength: 10 },
    phone: {
      type: 'string',
      title: 'Phone',
      maxLength: 11,
      mask: 'phone-ext',
    },
    email: { type: 'string', title: 'Email' },
    facility_type: {
      type: 'string',
      title: 'Facility Type',
      maxLength: 30,
      enum: [
        'Hospital',
        'Hospital Pediatric',
        'Group Practice',
        'Outpatient Facility',
        'Miscellaneous',
        'Hospital Specialty',
        'Extended Care',
        'Equipment Supplies',
        'Pharmacy',
        'Laboratory',
      ],
    },
    specialty: {
      type: 'string',
      title: 'Specialty',
      maxLength: 30,
      enum: [
        'Acute Care',
        'General Medical',
        'Long Term Acute Care',
        'Ambulatory Surgical Center',
        'Unspecified',
        'Federally Qualified Health Center',
        'Rehabilitation',
        'Skilled Nursing Facility',
        'Durable Medical Equipment',
        'Retail/Chain',
        'Government/Military/VA',
        'Urgent Care Center',
        'Clinic',
        'Physical Therapy Center',
        'OBGYN',
        'Oncology',
        'Surgical',
      ],
    },
    is_affiliated: { type: 'boolean', title: 'Affiliated' },
  },
};

const uiSchema = {
  npi: {
    'ui:widget': NpiWidget,
  },
  email: {
    'ui:classNames': 'two-column-field',
  },
  phone: {
    'ui:classNames': 'two-column-field',
  },
  facility_type: {
    'ui:classNames': 'two-column-field',
  },
  specialty: {
    'ui:classNames': 'two-column-field',
  },
  is_affiliated: {
    'ui:classNames': 'two-column-field',
  },
};

const NEW_ORGANIZATION = {
  is_affiliated: false,
};

class OrganizationEditDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
    };
  }

  render() {
    const {
      open,
      organizationData,
    } = this.props;

    if (organizationData) {
      organizationData.is_affiliated = (organizationData.affiliated
        && organizationData.affiliated.length > 0);
    }

    return (
      <DialogForm
        title={organizationData && organizationData.id > 0 ? 'Edit Organization' : 'Add Organization'}
        open={open}
        formData={organizationData || NEW_ORGANIZATION}
        schema={schema}
        uiSchema={uiSchema}
        onClose={this.props.onClose}
        onFormSubmit={this.onSave}
        onFormChange={this.onFormChange}
        formContext={{ npiError: this.state.npiError }}
        status={this.state.userDialogError ? this.state.userDialogErrorMessage : ''}
        statusIsError
      />
    );
  }

  // Clear the NPI error once the admin edits the NPI so the field doesn't stay
  // red after they've changed it.
  onFormChange = (form) => {
    if (
      this.state.npiError &&
      form && form.formData &&
      form.formData.npi !== this.state.npiErrorValue
    ) {
      this.setState({
        npiError: null,
        npiErrorValue: null,
      });
    }
  };

  onSave = (data) => {
    if (data.phone) {
      data.phone = PhoneUtil.unformat(data.phone);
      if (!data.phone || !PhoneUtil.isValid(data.phone)) {
        this.setState({
          userDialogError: true,
          userDialogErrorMessage: 'Invalid phone number.',
        });
        return;
      }
    }

    // Block save unless the NPI is exactly 10 digits. The message renders
    // directly under the NPI field (via NpiWidget + formContext).
    const npiDigits = (data.npi || '').replace(/\D/g, '');
    if (npiDigits.length !== 10) {
      this.setState({
        npiError: NPI_LENGTH_MESSAGE,
        npiErrorValue: data.npi,
      });
      return;
    }

    // Block save if the NPI is already assigned to a different organization.
    this.npiExists(data.npi, data.id, (exists) => {
      if (exists) {
        this.setState({
          npiError: DUPLICATE_NPI_MESSAGE,
          npiErrorValue: data.npi,
        });
        return;
      }

      this.setState({
        npiError: null,
        npiErrorValue: null,
      });
      this.props.onSave(data);
    });
  };

  npiExists(npi, id, callback) {
    if (!npi) {
      callback(false);
      return;
    }
    axiosInstance
      .get('/Organizations', {
        params: {
          opts: {
            where: {
              npi,
            },
          },
        },
      })
      .then((response) => {
        const match = response.data && response.data.find(
          (org) => org.id !== id,
        );
        callback(!!match);
      })
      .catch(() => {
        // If the lookup fails, don't block saving on a network error;
        // the backend uniqueness constraint remains the final guard.
        callback(false);
      });
  }
}

OrganizationEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationData: PropTypes.object,
};

const styled = withStyles(styles)(OrganizationEditDialog);
export { styled as OrganizationEditDialog };
